import { z } from 'zod';
// --- AI Profile Schemas ---
const providerConfigSchema = z.object({
    apiKey: z.string().max(500).optional(),
    baseUrl: z.string().url().max(500).optional(),
    sdkType: z.enum(['openai', 'anthropic']).optional(),
}).strict().optional();
const settingsSchema = z.object({
    provider: z.enum(['copilot', 'minimax', 'openai', 'custom']).optional(),
    providerConfig: providerConfigSchema,
    model: z.string().max(100).optional(),
    reasoningEffort: z.string().max(20).optional(),
    responseLanguage: z.string().max(20).optional(),
    systemPrompt: z.string().max(10000).optional(),
    customInstructionsEnabled: z.boolean().optional(),
    workingDirectory: z.string().max(500).optional(),
    workspacePath: z.string().max(500).optional(),
    timeout: z.number().int().min(1000).max(300000).optional(),
    maxBuffer: z.number().int().min(1024).optional(),
    maxToolRounds: z.number().int().min(1).max(50).optional(),
    maxEmptyArgsRetries: z.number().int().min(1).max(20).optional(),
    autopilot: z.boolean().optional(),
    maxAutopilotContinues: z.number().int().min(1).max(20).optional(),
    continuationTimeout: z.number().int().min(0).max(120000).optional(),
    outputFormat: z.enum(['text', 'json']).optional(),
    stream: z.enum(['on', 'off']).optional(),
    summaryEnabled: z.boolean().optional(),
    summaryThreshold: z.number().int().min(500).max(50000).optional(),
    summaryMaxLength: z.number().int().min(100).max(5000).optional(),
}).strict();
const permissionsSchema = z.object({
    allowAllTools: z.boolean().optional(),
    allowAllPaths: z.boolean().optional(),
    allowInternetAccess: z.boolean().optional(),
    allowShellAccess: z.boolean().optional(),
    allowedTools: z.array(z.string().max(200)).optional(),
    deniedTools: z.array(z.string().max(200)).optional(),
    allowedCategories: z.array(z.string().max(50)).optional(),
    allowedPaths: z.array(z.string().max(500)).optional(),
}).strict();
const cronTriggerSchema = z.object({
    schedule: z.string().min(1).max(100),
    task: z.string().min(1).max(200),
    enabled: z.boolean(),
    description: z.string().max(500).optional(),
});
const eventTriggerSchema = z.object({
    event: z.string().min(1).max(200),
    filter: z.record(z.unknown()).optional(),
    task: z.string().min(1).max(200),
    enabled: z.boolean(),
    description: z.string().max(500).optional(),
});
const triggersSchema = z.object({
    cron: z.array(cronTriggerSchema).optional(),
    events: z.array(eventTriggerSchema).optional(),
}).strict().nullable();
const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const createProfileSchema = z.object({
    name: z.string().min(1).max(100),
    slug: z.string().min(1).max(100).regex(slugRegex, 'Slug must be lowercase alphanumeric with hyphens only').optional(),
    description: z.string().max(500).optional(),
    avatarEmoji: z.string().max(10).optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Must be valid hex color').optional(),
    type: z.enum(['assistant', 'automation', 'api', 'developer']).optional(),
    visibility: z.enum(['global', 'personal']).optional(),
    settings: settingsSchema.optional(),
    permissions: permissionsSchema.optional(),
    maxDailyTokens: z.number().int().positive().nullable().optional(),
    maxDailyConversations: z.number().int().positive().nullable().optional(),
    maxConcurrentRequests: z.number().int().min(1).max(100).optional(),
    triggers: triggersSchema.optional(),
    isTemplate: z.boolean().optional(),
    templateCategory: z.string().max(50).optional(),
});
export const updateProfileSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    slug: z.string().min(1).max(100).regex(slugRegex, 'Slug must be lowercase alphanumeric with hyphens only').optional(),
    description: z.string().max(500).optional(),
    avatarEmoji: z.string().max(10).optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Must be valid hex color').optional(),
    type: z.enum(['assistant', 'automation', 'api', 'developer']).optional(),
    visibility: z.enum(['global', 'personal']).optional(),
    isActive: z.boolean().optional(),
    settings: settingsSchema.optional(),
    permissions: permissionsSchema.optional(),
    maxDailyTokens: z.number().int().positive().nullable().optional(),
    maxDailyConversations: z.number().int().positive().nullable().optional(),
    maxConcurrentRequests: z.number().int().min(1).max(100).optional(),
    triggers: triggersSchema.optional(),
    isTemplate: z.boolean().optional(),
    templateCategory: z.string().max(50).optional(),
});
export const profileListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).optional().default(1),
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
    type: z.enum(['assistant', 'automation', 'api', 'developer']).optional(),
    visibility: z.enum(['global', 'personal']).optional(),
    isActive: z.coerce.boolean().optional(),
    isTemplate: z.coerce.boolean().optional(),
    templateCategory: z.string().max(50).optional(),
    search: z.string().max(100).optional(),
});
export const profileIdParamSchema = z.object({
    id: z.coerce.number().int().positive(),
});
export const importProfileSchema = z.object({
    version: z.literal(1),
    exportedAt: z.string(),
    profile: z.object({
        name: z.string().min(1).max(100),
        slug: z.string().min(1).max(100).regex(slugRegex),
        description: z.string().max(500).nullable(),
        avatarEmoji: z.string().max(10),
        color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
        type: z.enum(['assistant', 'automation', 'api', 'developer']),
        visibility: z.enum(['global', 'personal']),
        settings: settingsSchema,
        permissions: permissionsSchema,
        triggers: triggersSchema,
        maxDailyTokens: z.number().int().positive().nullable(),
        maxDailyConversations: z.number().int().positive().nullable(),
        maxConcurrentRequests: z.number().int().min(1).max(100),
        isTemplate: z.boolean(),
        templateCategory: z.string().max(50).nullable(),
    }),
});
//# sourceMappingURL=aiProfile.schema.js.map